import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Button, Chip } from '@c2fo/liquidity';
import { LockAltIcon } from '@c2fo/liquidity/icons';
import { useServiceAccountsState, useApiContext } from '../../contexts/FileDeliveryContext';
import { ChangePasswordModal } from '../ChangePasswordModal';
import { BackLink } from '../BackLink';
import { Card } from '../Card';
import { CardHeader } from '../CardHeader';
import { CardSection } from '../CardSection';
import { DetailField } from '../DetailField';
import { FieldsContainer } from '../FieldsContainer';
import { LoadingText } from '../LoadingText';
import { NotFoundCard } from '../NotFoundCard';
import { PageContainer } from '../PageContainer';
import { useErrorState } from '../ErrorBanner';
import { listLocations } from '../../api/Locations';
import { listCredentials } from '../../api/Credentials';
import { listDeliveries } from '../../api/Deliveries';
import { formatDateWithTimezone } from '../../../../utils/dateFormat';

export const ServiceAccountView: React.FC = () => {
  const { accountUuid } = useParams<{ accountUuid: string }>();
  const [isChangePasswordModalOpen, setIsChangePasswordModalOpen] = useState(false);
  const [locationsCount, setLocationsCount] = useState<number | null>(null);
  const [credentialsCount, setCredentialsCount] = useState<number | null>(null);
  const [deliveriesCount, setDeliveriesCount] = useState<number | null>(null);
  const { addError } = useErrorState();
  const apiContext = useApiContext();
  const {
    currentAccount: account,
    isLoadingAccount: isLoading,
    fetchAccount,
    clearCurrentAccount,
    updatePassword,
  } = useServiceAccountsState();

  useEffect(() => {
    if (accountUuid) {
      // Construct full path: serviceAccounts/{accountUuid}
      fetchAccount(`serviceAccounts/${accountUuid}`);
    }

    return () => {
      clearCurrentAccount();
    };
  }, [accountUuid, fetchAccount, clearCurrentAccount]);

  useEffect(() => {
    if (account) {
      const fetchCounts = async () => {
        try {
          const [locationsRes, credentialsRes, deliveriesRes] = await Promise.all([
            listLocations(apiContext, { pageSize: 1 }),
            listCredentials(apiContext, { pageSize: 1 }),
            listDeliveries(apiContext, { pageSize: 1 }),
          ]);
          setLocationsCount(locationsRes.totalSize);
          setCredentialsCount(credentialsRes.totalSize);
          setDeliveriesCount(deliveriesRes.totalSize);
        } catch {
          addError({ id: 'fetch-counts-error', message: 'Failed to load usage statistics', type: 'error' });
        }
      };

      fetchCounts();
    }
  }, [account, addError, apiContext]);

  if (isLoading) {
    return <LoadingText />;
  }

  if (!account) {
    return (
      <PageContainer>
        <BackLink to="/file-delivery/serviceAccounts" label="Back to Service Accounts" />
        <NotFoundCard message="Service account not found or could not be loaded." />
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <BackLink to="/file-delivery/serviceAccounts" label="Back to Accounts" />

      <Card>
        <CardHeader
          chip={
            <Chip
              label={account.enabled ? 'Enabled' : 'Disabled'}
              type={account.enabled ? 'primary' : 'error'}
              variant="filled"
              size="xs"
            />
          }
          title={account.displayName}
          description={account.description}
          actions={
            <Button
              variant="primary"
              onClick={() => setIsChangePasswordModalOpen(true)}
              className="rounded-md text-white"
              size="sm"
            >
              <span className="flex items-center gap-1">
                <LockAltIcon fill="white" />
                Change Password
              </span>
            </Button>
          }
        />
        <FieldsContainer>
          <DetailField label="Display Name" value={account.displayName} />
          <DetailField label="Username" value={account.username} mono />
          <DetailField label="UID" value={account.uid} mono />
          <DetailField label="Resource Name" value={account.name} mono />
          <DetailField
            label="Created"
            value={account.createTime ? formatDateWithTimezone(account.createTime) : undefined}
          />
          <DetailField
            label="Last Updated"
            value={account.updateTime ? formatDateWithTimezone(account.updateTime) : undefined}
          />
        </FieldsContainer>
      </Card>

      <Card className="mt-6">
        <CardSection title="Usage Statistics">
          <div className="grid grid-cols-3 gap-6">
            <div className="text-center">
              <div className="text-3xl font-semibold text-gray-900">{locationsCount ?? '-'}</div>
              <div className="text-sm text-gray-500 mt-1">Locations</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-semibold text-gray-900">{credentialsCount ?? '-'}</div>
              <div className="text-sm text-gray-500 mt-1">Credentials</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-semibold text-gray-900">{deliveriesCount ?? '-'}</div>
              <div className="text-sm text-gray-500 mt-1">Deliveries</div>
            </div>
          </div>
        </CardSection>
      </Card>

      {account && (
        <ChangePasswordModal
          isOpen={isChangePasswordModalOpen}
          accountName={account.name}
          accountDisplayName={account.displayName}
          onClose={() => setIsChangePasswordModalOpen(false)}
          onSave={updatePassword}
          onError={(message) => addError({ id: 'change-password-error', message, type: 'error' })}
        />
      )}
    </PageContainer>
  );
};
